import { Injectable, Inject, Optional } from '@angular/core';
import { DOCUMENT_TRANSFORMER } from './tokens';
import { DxDocument } from '@digitaix/types';

export interface DocumentTransformer<T extends DxDocument> {
  collectionPath: string;
  send: (document: T) => any;
  receive: (document: any) => T;
}

export function MapToArray(...fieldPaths: string[]) {
  return originalDocument => {
    const newDocument = { ...originalDocument };
    for (const fieldPath of fieldPaths) {
      if (
        originalDocument.hasOwnProperty(fieldPath) &&
        originalDocument[fieldPath] &&
        typeof originalDocument[fieldPath] === 'object'
      ) {
        newDocument[fieldPath] = Object.values(originalDocument[fieldPath]);
      }
    }
    return newDocument;
  };
}

export function ArrayToMap(...fieldPaths: string[]) {
  return originalDocument => {
    const newDocument = { ...originalDocument };
    for (const fieldPath of fieldPaths) {
      if (
        originalDocument.hasOwnProperty(fieldPath) &&
        Array.isArray(originalDocument[fieldPath])
      ) {
        newDocument[fieldPath] = originalDocument[fieldPath].reduce(
          (map, item) => ({ ...map, [item.id]: item }),
          {}
        );
      }
    }
    return newDocument;
  };
}

@Injectable({ providedIn: 'root' })
export class DocumentTransformerService {
  public transformers = new Map<string, DocumentTransformer<any>>();

  constructor(
    @Optional() @Inject(DOCUMENT_TRANSFORMER) transformers: any | null
  ) {
    if (transformers && Array.isArray(transformers)) {
      for (const transformer of transformers) {
        this.transformers.set(transformer.collectionPath, transformer);
      }
    }
  }

  public send<D extends DxDocument>(collectionPath: string, document: D): any {
    if (this.transformers.has(collectionPath)) {
      return this.transformers.get(collectionPath).send(document);
    } else {
      return document;
    }
  }

  public receive<D extends DxDocument>(
    collectionPath: string,
    document: any
  ): D {
    if (this.transformers.has(collectionPath)) {
      return this.transformers.get(collectionPath).receive(document);
    } else {
      return document;
    }
  }
}
